import React, { useEffect, useState, useRef, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import "./CreateEditUser.scss";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { callAPI } from "../../../../axios";
import { Switch, Autocomplete, TextField } from "@mui/material";
import BasicModal from "../../../core/BaseComponents/Modal";
import { hasAdminAndSuperAdminRoles, isAdminOrSuperAdminRole, isEditingOwnUserAsSuperAdmin, readStoredUserDetails, isAutoModeViewerRoleCode, formatHotelDisplayName } from "../../../../utils/utils";

const ADMIN_ROLE_SID = 1;
const SUPER_ADMIN_ROLE_SID = 2;

const formatStoredAccessTill = (accessTillDate) =>
  accessTillDate ? new Date(accessTillDate).toISOString().split("T")[0] : "";

const rolesIncludeSuperAdmin = (roleObjs) => {
  if (!Array.isArray(roleObjs)) return false;
  return roleObjs.some((r) => Number(r?.sid ?? r?.id) === SUPER_ADMIN_ROLE_SID);
};

const rolesIncludeAdmin = (roleObjs) => {
  if (!Array.isArray(roleObjs)) return false;
  return roleObjs.some((r) => Number(r?.sid ?? r?.id) === ADMIN_ROLE_SID);
};

const isAutoModeViewerRole = (role) =>
  isAutoModeViewerRoleCode(role?.roleCode ?? role?.code);

const computeSuperAdminOtpRequired = ({
  roleList,
  editUserData,
  accessTillCurrent,
  isCreateFlow,
  isPendingApproval,
  isEditUserPage,
}) => {
  const hasSuperAdmin = roleList.some((r) => Number(r?.sid) === SUPER_ADMIN_ROLE_SID);
  const hasAdmin = roleList.some((r) => Number(r?.sid) === ADMIN_ROLE_SID);
  const hasBothNow = hasSuperAdmin && hasAdmin;

  if (isCreateFlow || isPendingApproval) {
    return hasSuperAdmin;
  }

  if (!isEditUserPage || !editUserData) {
    return hasSuperAdmin;
  }

  const hadSuperAdminInitially = rolesIncludeSuperAdmin(editUserData.roles);
  const hadAdminInitially = rolesIncludeAdmin(editUserData.roles);
  const hadBothInitially = hadSuperAdminInitially && hadAdminInitially;

  const initialAt = formatStoredAccessTill(editUserData.accessTillDate);
  const cur = String(accessTillCurrent ?? "").trim();
  const accessTillChanged = initialAt !== cur;

  // Removing Super Admin — no OTP
  if (hadSuperAdminInitially && !hasSuperAdmin) {
    return false;
  }

  if (!hasSuperAdmin) {
    return false;
  }

  // Access till changed (Super Admin still selected) — OTP
  if (accessTillChanged) {
    return true;
  }

  // Had Admin + Super Admin, removed Admin only → Super Admin only; access till unchanged — no OTP
  if (hadBothInitially && hasSuperAdmin && !hasAdmin) {
    return false;
  }

  // Had Super Admin only, added Admin → Admin + Super Admin — OTP
  if (hadSuperAdminInitially && !hadAdminInitially && hasBothNow) {
    return true;
  }

  // Assigning Super Admin to a user who did not have it
  if (!hadSuperAdminInitially && hasSuperAdmin) {
    return true;
  }

  return false;
};

const computeAutoModeViewerOtpRequired = ({
  roleList,
  editUserData,
  isCreateFlow,
  isPendingApproval,
  isEditUserPage,
}) => {
  const hasAutoModeRole = roleList.some(isAutoModeViewerRole);
  if (!hasAutoModeRole) return false;
  if (isCreateFlow || isPendingApproval) return true;
  if (!isEditUserPage || !editUserData) return true;
  const hadAutoModeRoleInitially = Array.isArray(editUserData.roles)
    && editUserData.roles.some(isAutoModeViewerRole);
  return !hadAutoModeRoleInitially;
};

const CreateEditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const loggedInUserDetails = readStoredUserDetails();
  const editingOwnUserAsSuperAdmin = isEditingOwnUserAsSuperAdmin(location?.state, loggedInUserDetails);

  const [cancelPopupClearHotel, setCancelPopupClearHotel] = useState(false);

  // This stores the function to run if "Yes" is clicked in modal
  const [pendingClear, setPendingClear] = useState(null);

  const [roles, setRoles] = useState([]);
  const [hotels, setHotelsList] = useState([]);
  const [editUserData, setEditUserData] = useState(null);
  const [cancelPopup, setCancelPopup] = useState(false);
  const [createUpdatePopUp, setCreateUpdatePopUp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const [resendOtpSecondsLeft, setResendOtpSecondsLeft] = useState(0);
  const resendOtpTimerRef = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
    control,
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: { enabled: true }
  });

  const today = new Date().toISOString().split("T")[0];

  const cancelPopupHandlerClearHotel = () => {
    if (pendingClear) pendingClear();
    setPendingClear(null);
    setCancelPopupClearHotel(false);
  };

  const onSubmit = (data) => {
    let method = "POST";
    let url = "/user";
    if (location?.state?.isEditUser) {
      method = "PUT";
      url = `user?sid=${location?.state?.sid}`;
    }
    if (location?.state?.isPendingApprovalStatus) {
      method = "PUT";
      url = `user/signup-approve?sid=${location?.state?.sid}`;
    }
    const roleList = Array.isArray(data.roles) ? data.roles : data.roles ? [data.roles] : [];
    const otpValue = String(data.otp ?? "").trim();

    if (editingOwnUserAsSuperAdmin) {
      if (!roleList.some((r) => Number(r?.sid) === SUPER_ADMIN_ROLE_SID)) {
        setError("roles", { type: "manual", message: "You cannot remove Super Admin from your own account." });
        return;
      }
      if (roleList.some((r) => !isAdminOrSuperAdminRole(r))) {
        setError("roles", { type: "manual", message: "You may only use Super Admin and Admin roles on your own account." });
        return;
      }
    }

    const hasAutoModeViewer = roleList.some(isAutoModeViewerRole);
    const showChainId = !hasAutoModeViewer && roleList.some((r) => Number(r?.sid) !== SUPER_ADMIN_ROLE_SID);
    let chainId = "";
    if (showChainId) {
      data?.chainId?.forEach((item, index) => {
        chainId = chainId + `${index ? "," : ""}${item?.chainID}`;
      });
    }

    const roleSids = roleList.map((r) => r.sid?.toString()).filter(Boolean);

    const isEditUserPage = Boolean(location?.state?.isEditUser);
    const isPendingApproval = Boolean(location?.state?.isPendingApprovalStatus);
    const isCreateFlow = !isEditUserPage && !isPendingApproval;
    const accessTillCurrent = String(data.accessTillDate ?? watch("accessTillDate") ?? "").trim();

    const needSuperAdminOtp = computeSuperAdminOtpRequired({
      roleList,
      editUserData,
      accessTillCurrent,
      isCreateFlow,
      isPendingApproval,
      isEditUserPage,
    });

    const needsAutoModeViewerOtp = computeAutoModeViewerOtpRequired({
      roleList,
      editUserData,
      isCreateFlow,
      isPendingApproval,
      isEditUserPage,
    });
    const needOtp = needSuperAdminOtp || needsAutoModeViewerOtp;

    if (needOtp && (!otpValue || !/^\d{6}$/.test(otpValue))) {
      setError("otp", {
        type: "manual",
        message: "OTP is required. Send OTP and enter the 6-digit code.",
      });
      return;
    }

    const payload = { ...data, chainId, roleSids, validationRequired: false };
    delete payload.roles;

    if (needOtp && /^\d{6}$/.test(otpValue)) {
      payload.otp = otpValue;
      payload.validationRequired = true;
    }

    if (editingOwnUserAsSuperAdmin && editUserData) {
      payload.firstName = editUserData.firstName;
      payload.lastName = editUserData.lastName ?? "";
      payload.phoneNo = editUserData.phoneNo;
      payload.enabled = editUserData.enabled;
      payload.accessTillDate = editUserData.accessTillDate
        ? new Date(editUserData.accessTillDate).toISOString().split("T")[0]
        : "";
    }

    callAPI(url, method, payload)
      .then(function (response) {
        setCreateUpdatePopUp(response.message);
      })
      .catch(function (error) {
        console.warn(error.response?.data?.errorMessage);
      });
  };

  const handleReset = () => {
    reset();
    setOtpSent(false);
    clearErrors("otpSendError");
    setOtpSendLoading(false);
    setResendOtpSecondsLeft(0);
    if (resendOtpTimerRef.current) {
      clearInterval(resendOtpTimerRef.current);
      resendOtpTimerRef.current = null;
    }
  };

  const rolesValue = watch("roles");
  const rolesArray = Array.isArray(rolesValue) ? rolesValue : rolesValue ? [rolesValue] : [];
  const isSuperAdminSelected = rolesArray.some((r) => Number(r?.sid) === SUPER_ADMIN_ROLE_SID);
  const isAdminSelected = rolesArray.some((r) => Number(r?.sid) === ADMIN_ROLE_SID);
  const isbothAdminAndSuperAdminSelected = isAdminSelected && isSuperAdminSelected;

  const accessTillWatched = watch("accessTillDate");
  const isEditUserPageUi = Boolean(location?.state?.isEditUser);
  const isPendingApprovalUi = Boolean(location?.state?.isPendingApprovalStatus);
  const isCreateFlowUi = !isEditUserPageUi && !isPendingApprovalUi;

  const superAdminOtpRequired = useMemo(
    () =>
      computeSuperAdminOtpRequired({
        roleList: rolesArray,
        editUserData,
        accessTillCurrent: String(accessTillWatched ?? "").trim(),
        isCreateFlow: isCreateFlowUi,
        isPendingApproval: isPendingApprovalUi,
        isEditUserPage: isEditUserPageUi,
      }),
    [rolesValue, editUserData, accessTillWatched, isCreateFlowUi, isPendingApprovalUi, isEditUserPageUi],
  );

  const needsAutoModeViewerOtp = computeAutoModeViewerOtpRequired({
    roleList: rolesArray,
    editUserData,
    isCreateFlow: isCreateFlowUi,
    isPendingApproval: isPendingApprovalUi,
    isEditUserPage: isEditUserPageUi,
  });
  const otpRequired = superAdminOtpRequired || needsAutoModeViewerOtp;
  const isAutoModeViewerSelected = rolesArray.some(isAutoModeViewerRole);

  const isAdminOrSuperAdminMode = (() => {
    const v = rolesValue;
    if (v == null) return false;
    const arr = Array.isArray(v) ? v : [v];
    return arr.length > 0 && arr.every((r) => isAdminOrSuperAdminRole(r));
  })();

  const { onChange: otpFormOnChange, ...otpFieldRegister } = register("otp", {
    setValueAs: (v) => String(v ?? "").replace(/\D/g, "").slice(0, 6),
    validate: (value) => {
      if (!otpRequired) return true;
      if (!value) return "OTP is required";
      if (!/^\d{6}$/.test(value)) return "Enter 6 digits only";
      return true;
    },
  });

  const handleOtpInputChange = (e) => {
    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
    otpFormOnChange(e);
  };

  const handleOtpKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1 && /\D/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSendOtp = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const targetUser = watch("userName")?.trim();
    if (!targetUser) {
      setError("otpSendError", { type: "manual", message: "Please enter the user's email first." });
      return;
    }
    clearErrors("otpSendError");
    setOtpSendLoading(true);
    const url = needsAutoModeViewerOtp
      ? `/user/generate-auto-mode-viewer-otp?targetUser=${encodeURIComponent(targetUser)}`
      : `/user/generate-superadmin-otp?targetUser=${encodeURIComponent(targetUser)}&multiRole=${isbothAdminAndSuperAdminSelected}`;
    callAPI(url, "POST")
      .then(() => {
        setOtpSent(true);
        clearErrors("otpSendError");
        setResendOtpSecondsLeft(60);
        if (resendOtpTimerRef.current) clearInterval(resendOtpTimerRef.current);
        resendOtpTimerRef.current = setInterval(() => {
          setResendOtpSecondsLeft((prev) => {
            if (prev <= 1) {
              if (resendOtpTimerRef.current) {
                clearInterval(resendOtpTimerRef.current);
                resendOtpTimerRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      })
      .catch((error) => {
        setError("otpSendError", { type: "manual", message: "Failed to send OTP." });
      })
      .finally(() => {
        setOtpSendLoading(false);
      });
  };

  const handleRolesChange = (newValue, field, multi) => {
    if (editingOwnUserAsSuperAdmin) {
      let next = Array.isArray(newValue) ? [...newValue] : newValue ? [newValue] : [];
      next = next.filter(isAdminOrSuperAdminRole);
      const superAdminOption = roles.find((r) => Number(r.sid) === SUPER_ADMIN_ROLE_SID);
      const nextHasSuperAdmin = next.some((r) => Number(r?.sid) === SUPER_ADMIN_ROLE_SID);
      if (superAdminOption && !nextHasSuperAdmin) {
        setError("roles", {
          type: "manual",
          message: "You cannot remove Super Admin from your own account.",
        });
        return;
      }
      clearErrors("roles");
      field.onChange(next);
      return;
    }

    if (multi) {
      field.onChange(newValue ?? []);
      return;
    }

    if (newValue && isAdminOrSuperAdminRole(newValue)) {
      field.onChange([newValue]);
    } else {
      field.onChange(newValue ?? null);
    }
  };

  // const handleCancelButton = (e) => {
  //   e.preventDefault();
  //   navigate(-1);
  // };

  // useEffect(() => {
  //   if (isAutoModeViewerSelected && hotels.length > 0) {
  //     setValue("chainId", hotels);
  //   }
  // }, [isAutoModeViewerSelected, hotels, setValue]);

  useEffect(() => {
    const v = rolesValue;
    const arr = Array.isArray(v) ? v : v ? [v] : [];
    const both = hasAdminAndSuperAdminRoles(arr);

    if (!both) {
      setOtpSent(false);
      clearErrors("otpSendError");
      setResendOtpSecondsLeft(0);
      if (resendOtpTimerRef.current) {
        clearInterval(resendOtpTimerRef.current);
        resendOtpTimerRef.current = null;
      }
    }
  }, [rolesValue, clearErrors]);

  useEffect(() => {
    return () => {
      if (resendOtpTimerRef.current) {
        clearInterval(resendOtpTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const apiPath = `/master?type=ROLES`;
    callAPI(apiPath, "GET")
      .then((res) => {
        const data = res.data;
        setRoles(data);
      })
      .catch((err) => {
        console.warn(err.message);
      });

    callAPI("/hotel/list", "GET")
      .then((res) => {
        const data = res.data;
        setHotelsList(data);
      })
      .catch((err) => {
        console.warn(err.message);
      });


  }, []);

  useEffect(() => {
    if (
      !location?.state?.isEditUser &&
      !location?.state?.isPendingApprovalStatus
    ) {
      return;
    }

    const sid = location?.state?.sid;
    if (!sid) return;

    callAPI(`user?sid=${sid}`, "GET")
      .then(function (response) {
        setEditUserData(response);
      })
      .catch(function (error) {
        console.warn(error.response?.data?.errorMessage);
      });
  }, [
    location?.state?.sid,
    location?.state?.isEditUser,
    location?.state?.isPendingApprovalStatus,
  ]);

  useEffect(() => {
    if (!editUserData) return;

    const role = roles?.filter((item) =>
      editUserData?.roles?.map((r) => r.id)?.includes(item?.sid)
    ) || [];
    const hotelApiList =
      editUserData?.chainIdHotelNameMapping?.map((hotel) => hotel?.chainId) || [];
    const hotel = hotels?.filter((item) => hotelApiList.includes(item?.chainID)) || [];
    const accessTillDate = editUserData?.accessTillDate
      ? new Date(editUserData.accessTillDate).toISOString().split("T")[0]
      : "";

    const rawEnabled = editUserData?.enabled;
    const enabledBool =
      rawEnabled === false ||
      rawEnabled === 0 ||
      (typeof rawEnabled === "string" &&
        ["false", "0"].includes(String(rawEnabled).toLowerCase()))
        ? false
        : true;

    const payload = {
      userName: editUserData?.userName,
      accessTillDate,
      enabled: enabledBool,
      firstName: editUserData?.firstName,
      lastName: editUserData?.lastName,
      phoneNo: editUserData?.phoneNo,
      chainId: hotel,
      roles: role,
    };

    reset(payload);
  }, [editUserData, roles, hotels, reset]);

  const closePopupHandler = () => {
    setCreateUpdatePopUp("");
    navigate("/hms/userList");
  };

  const cancelPopupHandler = () => {
    setCancelPopup(false)
    navigate("/hms/userList");
  }

  return (
    <>
      <div className="breadcrumb">
        <ul>
          <li>
            <Link to="/hms/ConfigurationNewHotel">Dashboard </Link>{" "}
            <span className="arrow-color"> &gt; </span>
          </li>
          {location?.state?.isPendingApprovalStatus || location?.state?.isEditUser && <li>
            <Link to="/hms/userList">User Management List</Link>{" "}
            <span className="arrow-color"> &gt; </span>
          </li>}
          <li>
            {location?.state?.isPendingApprovalStatus
              ? "Approve User"
              : location?.state?.isEditUser
                ? "Edit User"
                : "Create User"}
          </li>
        </ul>
      </div>
      <div className="sub-header">
        <h1>
          {location?.state?.isPendingApprovalStatus
            ? "Approve User"
            : location?.state?.isEditUser
              ? "Edit User"
              : "Create User"}
        </h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="create-edit-user">
        <div className="row">
          <div className="col-4">
            <label htmlFor="userName">Email <span className="required">*</span></label>
            <input
              id="userName"
              className="form-control"
              placeholder="Enter Email"
              disabled={location?.state?.isEditUser || location?.state?.isPendingApprovalStatus}
              maxLength={60}
              {...register("userName", {
                required: "Please Enter Your Email",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please Enter Email in correct format",
                },
              })}
            />
            {errors.userName && (
              <p className="required">{errors.userName.message}</p>
            )}
          </div>

          <div className="col-4">
            <label htmlFor="firstName">First Name<span className="required">*</span></label>
            <input
              id="firstName"
              className="form-control"
              placeholder="Enter First Name"
              type="text"
              maxLength={30}
              disabled={editingOwnUserAsSuperAdmin}
              {...register("firstName", {
                required: "Enter First Name",
                pattern: {
                  value: /^[A-Za-z]+(?:[A-Za-z]+)*$/,
                  message: 'Enter Alphabets only',
                }
              })}
            />
            {errors.firstName && (
              <p className="required">{errors.firstName.message}</p>
            )}
          </div>

          <div className="col-4">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              className="form-control"
              placeholder="Enter Last Name"
              type="text"
              maxLength={30}
              disabled={editingOwnUserAsSuperAdmin}
              {...register("lastName", {
                pattern: {
                  value: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
                  message: 'Enter Alphabets only',
                }
              })}
            />
            {errors.lastName && (
              <p className="required">{errors.lastName.message}</p>
            )}
          </div>

          <div className="col-4">
            <label htmlFor="phoneNo">Phone<span className="required">*</span></label>
            <input
              id="phoneNo"
              className="form-control"
              placeholder="Enter Phone Number"
              type="tel"
              disabled={editingOwnUserAsSuperAdmin}
              // maxLength={12}
              // minLength={10}
              {...register("phoneNo", {
                required: "Please Enter Phone Number",
                pattern: {
                  value: /^[0-9]{10,12}$/,
                  message: "Phone number must be between 10 and 12 digits",
                },
              })}
            />
            {errors.phoneNo && (
              <p className="required">{errors.phoneNo.message}</p>
            )}
          </div>

          <div className="col-4">
            <label htmlFor="roles">User Role<span className="required">*</span></label>
            <Controller
              name="roles"
              control={control}
              rules={{
                validate: (value) => {
                  const hasValue = Array.isArray(value) ? value.length > 0 : value != null && value !== "";
                  return (location?.state?.isPendingApprovalStatus ? hasValue : hasValue) || "Selecting a role is required";
                },
              }}
              render={({ field }) => {
                const multi = isAdminOrSuperAdminMode;
                const value = multi
                  ? (Array.isArray(field.value) ? field.value : field.value ? [field.value] : [])
                  : (Array.isArray(field.value) ? field.value[0] ?? null : field.value ?? null);
                return (
                  <Autocomplete
                    {...field}
                    className="select-dropdown"
                    disablePortal
                    multiple={multi}
                    options={editingOwnUserAsSuperAdmin ? roles.filter(isAdminOrSuperAdminRole) : roles}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, val) => option?.sid === val?.sid}
                    getOptionDisabled={multi ? (option) => !isAdminOrSuperAdminRole(option) : undefined}
                    onChange={(_, newValue) => handleRolesChange(newValue, field, multi)}
                    value={value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={multi ? "Add Another Role" : "Please Select User Role"}
                      />
                    )}
                  />
                );
              }}
            />
            {errors.roles && (
              <p className="required">{errors.roles.message}</p>
            )}
          </div>

          {rolesArray.length > 0 && rolesArray.some((r) => Number(r?.sid) !== SUPER_ADMIN_ROLE_SID) && !isAutoModeViewerSelected && (
            <div className="col-4">
              <label htmlFor="chainId">Link Hotel<span className="required">*</span></label>
              {/* <select
              id="chainId"
              className="form-control"
              {...register("chainId", {
                required: "Linking a Hotel is required",
              })}
            >
              <option className="custom-placeholder" hidden value="">Please Link Hotel...</option>
              {hotels?.map((hotel) => (
                <option key={hotel?.id} value={hotel?.chainID}>
                  {hotel?.hotelName}
                </option>
              ))}
            </select> */}
              <Controller
                name="chainId"
                control={control}
                rules={{
                  validate: (value) =>
                    (location?.state?.isPendingApprovalStatus
                      ? Object.entries(value === null ? {} : value).length > 0
                      : value !== undefined && value !== null && value.length > 0) || "Linking a Hotel is required",
                }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    className="select-dropdown"
                    limitTags={1}
                    disablePortal
                    multiple
                    disableCloseOnSelect
                    disabled={isAutoModeViewerSelected}
                    options={hotels}
                    getOptionLabel={formatHotelDisplayName}
                    isOptionEqualToValue={(option, value) => option.chainID === value?.chainID}
                    onChange={(_, newValue, reason) => {
                      if (isAutoModeViewerSelected) {
                        field.onChange(hotels);
                        return;
                      }
                      if (reason === "clear") {
                        setPendingClear(() => () => field.onChange([])); // store callback
                        setCancelPopupClearHotel(true); // open modal
                      } else {
                        field.onChange(newValue);
                      }
                    }}
                    value={field.value || []}
                    renderInput={(params) => <TextField {...params} placeholder="Please Link Hotels" />}
                  />
                )}
              />
              {errors.chainId && (
                <p className="required">{errors.chainId.message}</p>
              )}
            </div>
          )}

          <div className="col-4">
            <label htmlFor="accessTillDate">Access Till Date</label>
            <input
              id="accessTillDate"
              type="date"
              className="form-control"
              min={today}
              disabled={editingOwnUserAsSuperAdmin}
              {...register("accessTillDate", {
                required: "Access Till Date is required",
              })}
            />
            {errors.accessTillDate && (
              <p className="required">{errors.accessTillDate.message}</p>
            )}
          </div>

          {otpRequired && (
            <>
              <div className="col-4">
                <label htmlFor="otp">Enter OTP here<span className="required">*</span></label>
                <div className="otp-input-with-send">
                  <input
                    id="otp"
                    className="otp-input-inner"
                    placeholder="Enter OTP"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    {...otpFieldRegister}
                    onChange={handleOtpInputChange}
                    onKeyDown={handleOtpKeyDown}
                  />
                  <button
                    type="button"
                    className="send-otp-inside"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendOtp(e);
                    }}
                    disabled={otpSendLoading || resendOtpSecondsLeft > 0}
                  >
                    {otpSendLoading ? "Sending..." : resendOtpSecondsLeft > 0 ? `Resend (${resendOtpSecondsLeft}s)` : "Send OTP"}
                  </button>
                </div>
                {errors.otp && (
                  <p className="required">{errors.otp.message}</p>
                )}
                {errors.otpSendError && (
                  <p className="required mb-0">{errors.otpSendError.message}</p>
                )}
                {otpSent && (
                  <p className="mb-0 text-success">OTP has been sent to the user&apos;s email.</p>
                )}
                {resendOtpSecondsLeft > 0 && (
                  <p className="mb-0 text-muted small">Resend OTP after {resendOtpSecondsLeft} seconds.</p>
                )}
              </div>
            </>
          )}

          <div className="col-12">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <>
                  <label htmlFor="enabled">
                    {" "}
                    User {!field.value ? "Disabled" : "Enabled"}
                  </label>
                  <Switch
                    id="enabled"
                    className={`form-control border-0 ${
                      !field.value ? "switchuser-inactive" : "switchuser-active"
                    }`}
                    checked={!!field.value}
                    onChange={(_, checked) => field.onChange(checked)}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    disabled={editingOwnUserAsSuperAdmin}
                  />
                </>
              )}
            />
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn_new"
              onClick={() => setCancelPopup(true)}
            >
              Cancel
            </button>
            {!location?.state?.isPendingApprovalStatus && !editingOwnUserAsSuperAdmin && (
              <button type="button" className="reset-btn" onClick={handleReset}>
                Reset
              </button>
            )}
            <button
              type="submit"
              className="create-btn"
            >
              {location?.state?.isPendingApprovalStatus
                ? "Approve"
                : location?.state?.isEditUser
                  ? "Update"
                  : "Create"}
            </button>
          </div>
        </div>
      </form>
      <BasicModal
        popupOpen={createUpdatePopUp !== ""}
        bodyMessage={<>
          {createUpdatePopUp && (
            <span className="d-block">
              <img src="/Images/approve-qc.png" alt="approve" style={{ width: 60 }} />
              <p className="mt-2">{createUpdatePopUp}</p>
            </span>
          )}
        </>
        }
        footer={
          {
            secondaryButton: {
              buttonName: "OK",
              action: closePopupHandler
            }
          }
        }
      />
      <BasicModal
        popupOpen={cancelPopup}
        bodyMessage={"Do you really want to cancel it?"}
        logo={"/Images/Rejected.png"}
        footer={
          {
            primaryButton: {
              buttonName: "Cancel",
              action: () => setCancelPopup(false)
            },
            secondaryButton: {
              buttonName: "Yes",
              action: cancelPopupHandler
            }
          }
        }
      />
      <BasicModal
        popupOpen={cancelPopupClearHotel}
        bodyMessage={"Do you really want to unlink all Hotels?"}
        logo={"/Images/Rejected.png"}
        footer={{
          primaryButton: {
            buttonName: "Cancel",
            action: () => {
              setCancelPopupClearHotel(false);
              setPendingClear(null);
            },
          },
          secondaryButton: {
            buttonName: "Yes",
            action: cancelPopupHandlerClearHotel,
          },
        }}
      />
    </>
  );
};

export default CreateEditUser;
